import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ColDef, GridOptions, GridReadyEvent, IDatasource, IGetRowsParams, ITextFilterParams } from 'ag-grid-community';
import { firstValueFrom } from 'rxjs';

import { PublicApiService } from '../../services/public-api.service';
import { Fleet } from '../../models/fleet.model';
import { Alert, Location } from '../../models/location.model';
import { FleetSelectorComponent } from '../fleet-selector/fleet-selector.component';
import { AddLocationComponent } from '../add-location/add-location.component';
import { MoveHubComponent } from '../move-hub/move-hub.component';
import { ChangeFleetComponent } from '../change-fleet/change-fleet.component';
import { AddUserComponent } from '../add-user/add-user.component';
import { environment } from '../../../environments/environment';

// Make Math available to the template
declare global {
  interface Window {
    Math: typeof Math;
  }
}

declare var bootstrap: any;

@Component({
  selector: 'app-locations',
  templateUrl: './locations.component.html',
  styleUrls: ['./locations.component.scss'],
})
export class LocationsComponent implements OnInit, OnDestroy {
  private dataSource: IDatasource = {
    getRows: (params: IGetRowsParams) => {
      if (!this.selectedFleet) {
        params.failCallback();
        return;
      }

      const startRow = params.startRow;
      const pageSize = params.endRow - params.startRow;

      // Calculate the page number (1-based)
      const page = Math.floor(startRow / pageSize) + 1;

      console.log(`Loading rows ${startRow} to ${params.endRow}`);

      const filters = JSON.stringify(params.filterModel ?? {});
      const sorters = JSON.stringify(params.sortModel ?? {});

      const request = this.searchTerm
        ? this.publicApiService.searchFleetLocations(this.selectedFleet.uuid, this.searchTerm, page, pageSize)
        : this.publicApiService.getFleetLocationsWithTotal(this.selectedFleet.uuid, page, pageSize, '_id', filters, sorters);

      request.subscribe({
        next: response => {
          const totalRows = response.total;

          let locationUuids: any[] = [];

          if (response.data) {
            locationUuids = response.data;
          } else if (Array.isArray(response)) {
            locationUuids = response;
          } else {
            locationUuids = [];
          }

          // Fetch details for each location
          if (response.total === 0) {
            this.numRows = response.total;
            params.successCallback([], 0);
            return;
          }

          // Ensure locationUuids is an array of strings
          locationUuids = this.processLocationList(locationUuids);

          const detailedLocations: Location[] = [];
          let completedRequests = 0;

          locationUuids.forEach((uuid: string) => {
            // console.log(`fetching location details for UUID ${uuid}:`);
            this.publicApiService.getLocationDetails(uuid).subscribe({
              next: locationData => {
                detailedLocations.push(locationData);
                completedRequests++;
                // console.log(`loading location details for UUID ${uuid}:`);
                if (completedRequests === locationUuids.length) {
                  // Sort detailed locations to match original UUID order
                  const sortedLocations = locationUuids
                    .map(uuid => detailedLocations.find(loc => loc.uuid === uuid))
                    .filter((loc): loc is Location => loc !== undefined);

                  this.updatingData = false;
                  this.numRows = response.total;
                  params.successCallback(sortedLocations, response.total);
                }
              },
              error: err => {
                this.updatingData = false;
                console.error(`Error loading location details for UUID ${uuid}:`, err);
                completedRequests++;
                if (completedRequests === locationUuids.length) {
                  params.successCallback(detailedLocations, totalRows);
                }
              },
            });
          });
        },
        error: err => {
          console.error('Error loading locations:', err);
          params.failCallback();
        },
      });
    },
  };

  // Make Math available to the template
  Math = Math;

  // Reference to the fleet-selector component
  @ViewChild(FleetSelectorComponent) fleetSelector!: FleetSelectorComponent;

  // Reference to the add-location component
  @ViewChild(AddLocationComponent) addLocationComponent!: AddLocationComponent;
  @ViewChild(MoveHubComponent) moveHubComponent!: MoveHubComponent;
  @ViewChild(ChangeFleetComponent) changeFleetComponent!: ChangeFleetComponent;
  @ViewChild(AddUserComponent) addUserComponent!: AddUserComponent;

  // Fleet data
  selectedFleet: Fleet | null = null;
  selectedLocation: Location | null = null;
  selectedLocationDetails: any = {};
  previousSelectedFleet: Fleet | null = null;
  selectedHubId: string = '';
  numRows = 0;
  loading: boolean = false;
  updatingData: boolean = false;
  error: string = '';
  isFiltered: boolean = false;
  showMutedAlerts: boolean = true;
  mutatingAlertKeys = new Set<string>();
  openMuteDropdownAlertKey: string | null = null;
  customMuteUntilByAlertKey: Record<string, string> = {};
  isMutatingAllAlerts: boolean = false;
  isLocationWideMuteDropdownOpen: boolean = false;
  customMuteAllUntil: string = '';
  isCustomMuteAllVisible: boolean = false;
  selectedQcConfirmSection: string = '';
  selectedQcConfirmScope: string = '';
  selectedQcConfirmItem: any = null;
  selectedQcConfirmValue: boolean = false;
  isUpdatingQcScanConfirmation: boolean = false;

  // Store reference to event listener for cleanup
  private toggleRowListener!: EventListener;

  // Search
  searchTerm: string = '';

  // Pagination
  currentPage: number = 1;

  // AG Grid
  columnDefs: ColDef[] = [
    {
      field: 'alerts',
      headerName: '',
      // use a value getter to have the node ID as this column's value
      valueGetter: 'node.id',
      width: 180,
      // then a custom cellRenderer
      cellRenderer: (params: any) => {
        if (params.value === undefined) {
          return '<div class="spinner-border spinner-border-sm text-success" role="status"></div>';
        }

        const location = params?.data as Location | undefined;
        const mutingStatus = location ? this.getLocationMutingStatusForLocation(location) : null;
        const iconSvg = params?.data.alerts?.find((a: any) => a.type === 'error')
          ? `<svg width="24" height="24" fill="red"
             class="bi bi-exclamation-triangle-fill flex-shrink-0 mt-2">
            <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96
                     0L.165 13.233c-.457.778.091 1.767.98
                     1.767h13.713c.889 0 1.438-.99.98-1.767L8.982
                     1.566zM8 5c.535 0 .954.462.9.995l-.35
                     3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905
                     0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
        </svg>`
          : params?.data.alerts?.find((a: any) => a.type === 'alert')
            ? `<svg width="24" height="24"
             fill="orange"
             class="bi bi-exclamation-triangle-fill flex-shrink-0 mt-2">
            <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96
                     0L.165 13.233c-.457.778.091 1.767.98
                     1.767h13.713c.889 0 1.438-.99.98-1.767L8.982
                     1.566zM8 5c.535 0 .954.462.9.995l-.35
                     3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905
                     0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
        </svg>`
            : '';

        const badgeHtml = mutingStatus
          ? `<span data-cy="location-grid-status-tag-${location?.uuid}" class="location-muting-badge location-muting-badge-grid ${mutingStatus === 'Partially Muted' ? 'location-muting-badge-partial' : ''}">${mutingStatus}</span>`
          : '';

        const iconQC = ((params?.data.number_of_hub_qc_errors>0 && params?.data.number_of_enphase_qc_errors>0)||
          (params?.data.hubs.length===0 && params?.data.number_of_enphase_qc_errors>0)
          )
          ? `<span class="badge bg-danger">QC</span>`
            :  (params?.data.number_of_qc_errors>0)
              ? `<span class="badge bg-warning">QC</span>`
                : '';

        if(params?.data.number_of_qc_errors>0){
          console.log('location with QC errors', params?.data);
        }

        if (!iconSvg && !badgeHtml && !iconQC) {
          return '';
        }

        return `<div data-cy="location-grid-status-${location?.uuid}" class="location-status-cell">${iconSvg}${badgeHtml}${iconQC}</div>`;
      },
      sortable: false,
      filter: true,
      filterParams: {
        filterOptions: [
          'empty',
          {
            displayKey: 'withQCAlerts',
            displayName: 'With QC Alerts',
            predicate: () => true,
            numberOfInputs: 0,
          },
          {
            displayKey: 'withQCErrors',
            displayName: 'With QC Errors',
            predicate: () => true,
            numberOfInputs: 0,
          },
          {
            displayKey: 'withNoAlerts',
            displayName: 'With No Alerts',
            predicate: () => true,
            numberOfInputs: 0,
          },
          {
            displayKey: 'withWarnings',
            displayName: 'With Warnings',
            predicate: () => true,
            numberOfInputs: 0,
          },
          {
            displayKey: 'withErrors',
            displayName: 'With Errors',
            predicate: () => true,
            numberOfInputs: 0,
          },
          {
            displayKey: 'Muted',
            displayName: 'Muted',
            predicate: () => true,
            numberOfInputs: 0,
          },
          {
            displayKey: 'PartiallyMuted',
            displayName: 'Partially Muted',
            predicate: () => true,
            numberOfInputs: 0,
          },
          {
            displayKey: 'FullyMuted',
            displayName: 'Fully Muted',
            predicate: () => true,
            numberOfInputs: 0,
          },
          {
            displayKey: 'Unmuted',
            displayName: 'Unmuted',
            predicate: () => true,
            numberOfInputs: 0,
          },
        ],
        maxNumConditions: 1,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      } as ITextFilterParams,
    },
    {
      field: 'label',
      headerName: 'Label',
      sortable: true,
      filter: true,
      filterParams: {
        filterOptions: ['contains'],
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        maxNumConditions: 1,
      },
      width: 300,
    },
    {
      field: 'fleet',
      headerName: 'Fleet',
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        // Get the fleet UUID from the cell value
        const fleetUuid = params.value;
        if (!fleetUuid) {
          return 'Unknown';
        }

        // Use the fleet selector to look up the fleet ID from the UUID
        try {
          // Check if the fleet selector is available
          if (this.fleetSelector) {
            return this.fleetSelector.getFleetIdByUuid(fleetUuid);
          } else {
            console.warn('Fleet selector not available, using UUID as fallback');
            return fleetUuid;
          }
        } catch (error) {
          console.error('Error looking up fleet ID:', error);
          return fleetUuid; // Fallback to UUID if lookup fails
        }
      },
    },
    {
      field: 'address',
      headerName: 'Address',
      sortable: true,
      filter: true,
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
    {
      field: 'city',
      headerName: 'City',
      sortable: true,
      filter: true,
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
    {
      field: 'state',
      headerName: 'State',
      sortable: true,
      filter: true,
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
      width: 120,
    },
    {
      field: 'postcode',
      headerName: 'Postal Code',
      sortable: true,
      filter: true,
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
      width: 120,
    },
    {
      field: 'hubs',
      headerName: 'Hub IDs',
      sortable: false,
      filter: true,
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
      wrapText: true,
      cellRenderer: (params: any) => {
        if (!params.data || !params.data.hubs || params.data.hubs.length === 0) {
          return 'No hubs';
        }

        const hubIds = params.data.hubs.map((hub: any) => hub.id);

        // Create a div element to hold the HTML content
        const div = document.createElement('div');

        div.innerHTML = hubIds.join(', ');

        return div;
      },
    },
    {
      field: 'users',
      headerName: 'Users',
      sortable: false,
      filter: false,
      wrapText: true,
      cellRenderer: (params: any) => {
        if (!params.data || !params.data.users || params.data.users.length === 0) {
          return 'No users';
        }
        const emailList = params.data.users.map((user: any) => user.email_address).join('<br>');

        // Create a div element to hold the HTML content
        const div = document.createElement('div');
        div.innerHTML = emailList;
        return div;
      },
    },
    {
      field: 'webApp',
      headerName: 'Web App',
      sortable: false,
      filter: false,
      width: 120,
      cellRenderer: (params: any) => {
        // Create a button element
        const button = document.createElement('button');
        button.innerHTML = 'Open';
        button.className = 'btn btn-primary btn-sm';

        // Add click event to open webAppUrl in a new tab
        button.addEventListener('click', () => {
          window.open(`${environment.webAppUrl}/?lid=${params.data.uuid}`, '_blank');
        });

        return button;
      },
    },
  ];

  defaultColDef: ColDef = {
    flex: 1,
    // minWidth: 100,
    resizable: true,
    wrapText: true,
  };

  gridApi: any;

  // Update grid configuration
  gridOptions: GridOptions = {
    rowModelType: 'infinite',
    cacheBlockSize: 15, // Number of rows per block
    maxBlocksInCache: 5,
    rowBuffer: 0,
    infiniteInitialRowCount: -1, // Initial row count estimate
    getRowId: params => params.data.uuid,
    datasource: this.dataSource,
    // Enable row selection
    rowSelection: 'single',
    // Add row class rules to highlight the selected row
    rowClassRules: {
      'selected-row': params => {
        return this.selectedLocation && params.data && params.data.uuid === this.selectedLocation.uuid;
      },
    },
    // autoSizeStrategy:{type: 'fitCellContents'}
    autoSizeStrategy: {
      // type: 'fitCellContents',
      type: 'fitGridWidth',
      defaultMinWidth: 80,
      // columnLimits: [
      //   {
      //     colId: 'label',
      //     minWidth: 200
      //   }
      // ]
    },
  };

  constructor(private publicApiService: PublicApiService) {}

  processLocationList(locationUuidsInput: any[]) {
    let locationUuids: any[] = locationUuidsInput;

    // Ensure locationUuids is an array of strings
    if (Array.isArray(locationUuids)) {
      // If it's already an array, make sure all elements are strings
      // Check if elements are objects with uuid field (new API format)
      if (locationUuids.length > 0 && typeof locationUuids[0] === 'object' && locationUuids[0] !== null && 'uuid' in locationUuids[0]) {
        locationUuids = locationUuids.map((item: any) => String(item.uuid));
      } else {
        locationUuids = locationUuids.map((uuid: any) => String(uuid));
      }
    } else if (typeof locationUuids === 'object') {
      // If it's an object, try to extract UUIDs from it
      console.log('Attempting to extract UUIDs from object');
      try {
        const locationData = locationUuids as { data: any[] };
        if ('data' in locationUuids && Array.isArray(locationData.data)) {
          // Check if elements are objects with uuid field (new API format)
          if (
            locationData.data.length > 0 &&
            typeof locationData.data[0] === 'object' &&
            locationData.data[0] !== null &&
            'uuid' in locationData.data[0]
          ) {
            locationUuids = locationData.data.map((item: any) => String(item.uuid));
          } else {
            locationUuids = locationData.data.map((uuid: any) => String(uuid));
          }
        } else {
          // If we can't extract UUIDs, create an empty array
          console.error('Could not extract UUIDs from object:', locationUuids);
          locationUuids = [];
        }
      } catch (error) {
        console.error('Error extracting UUIDs from object:', error);
        locationUuids = [];
      }
    } else {
      // If it's neither an array nor an object, create an empty array
      console.error('Invalid locationUuids type:', typeof locationUuids);
      locationUuids = [];
    }

    return locationUuids;
  }

  ngOnInit(): void {
    // Initial setup

    // Create and store event listener for row toggle
    this.toggleRowListener = ((event: CustomEvent) => {
      // Refresh the grid to update the display
      if (this.gridApi) {
        this.gridApi.refreshCells({ force: true });
      }
    }) as EventListener;

    // Add event listener
    document.addEventListener('toggle-row', this.toggleRowListener);
  }

  ngOnDestroy(): void {
    // Remove event listener to prevent memory leaks
    document.removeEventListener('toggle-row', this.toggleRowListener);
  }

  async onFleetSelected(fleet: Fleet): Promise<void> {
    this.updatingData = true;
    this.previousSelectedFleet = this.selectedFleet;

    this.currentPage = 1;

    this.selectedFleet = fleet;

    // Clear the selected location when a new fleet is selected
    this.selectedLocation = null;

    // Clear the selected hub ID when a new fleet is selected
    this.selectedHubId = '';

    if (this.gridApi) {
      this.gridApi.setGridOption('datasource', this.dataSource);
    }
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;

    // Auto-size columns to fit the available width
    this.gridApi.sizeColumnsToFit();
  }

  async onSearch(): Promise<void> {
    this.currentPage = 1;
    if (!this.selectedFleet) {
      return;
    }

    // Clear the selected location when search term changes
    this.selectedLocation = null;

    // Clear the selected hub ID when search term changes
    this.selectedHubId = '';

    if (this.gridApi) {
      this.gridApi.setGridOption('datasource', this.dataSource);
    }
  }

  async clearSearch(): Promise<void> {
    this.searchTerm = '';
    // The selectedLocation will be cleared in onSearch()
    await this.onSearch();
  }

  async onRowClicked(event: any): Promise<void> {
    // Set the selected location to the data of the clicked row
    this.selectedLocation = event.data;
    console.log('Selected location:', this.selectedLocation);

    // Clear the selected hub ID when a new location is selected
    this.selectedHubId = '';

    // Refresh the grid to apply the selected row styling
    if (this.gridApi) {
      this.gridApi.refreshCells({ force: true });
    }

    if(this.selectedLocation){
      this.selectedLocationDetails = {};
      Promise.all(
        [
          this.publicApiService.getLocationSFRDetails(this.selectedLocation?.uuid),
          this.publicApiService.getLocationProjectRecordDetails(this.selectedLocation?.uuid),
          this.publicApiService.getLocationDevicesDetails(this.selectedLocation?.uuid),
          this.publicApiService.getLocationQcData(this.selectedLocation?.uuid)
        ]
      ).then((values) => {
        const [sfrData, projectRecordData, devicesData, qcData] = values;
        this.selectedLocationDetails = {
          sfrData, projectRecordData, devicesData, qcData
        }
        console.log(this.selectedLocationDetails)
      })
    }

  }

  onConfigHubWebButtonClick(hubId: any) {
    window.open(`${environment.hubAppUrl}/?hid=${hubId}`, '_blank');
  }

  onConfigHubButtonClick(hubId: any) {
    this.selectedHubId = hubId;
    console.log('Selected hub ID:', this.selectedHubId);
  }

  onMoveHubButtonClick() {
    // This method will be handled by the move-hub component
    if (this.moveHubComponent) {
      this.moveHubComponent.openMoveHubModal(this.selectedFleet, this.selectedLocation);
    }
  }

  onChangeFleetButtonClick() {
    // This method will be handled by the change-fleet component
    if (this.changeFleetComponent) {
      this.changeFleetComponent.openChangeFleetModal(this.selectedLocation);
    }
  }

  openAddLocationModal() {
    // This method will now be handled by the add-location component
    if (this.addLocationComponent) {
      this.addLocationComponent.openAddLocationModal();
    }
  }

  onAddUserButtonClick() {
    // This method will be handled by the add-user component
    if (this.addUserComponent) {
      this.addUserComponent.openAddUserModal();
    }
  }

  hasError(location: Location): boolean {
    return location?.alerts?.find((a: Alert) => a.type === 'error') ? true : false;
  }

  isMutatingAlert(alert: Alert): boolean {
    return this.mutatingAlertKeys.has(this.getAlertKey(alert));
  }

  isAlertMuted(alert: Alert): boolean {
    const alertRules = alert.muting_rules ?? [];
    const locationWideRules = this.selectedLocation?.location_wide_muting_rules ?? [];
    return alertRules.length > 0 || locationWideRules.length > 0;
  }

  isLocationWideMuted(): boolean {
    return (this.selectedLocation?.location_wide_muting_rules ?? []).length > 0;
  }

  isPartiallyMuted(): boolean {
    if (this.isLocationWideMuted()) {
      return false;
    }
    return (this.selectedLocation?.alerts ?? []).some(alert => (alert.muting_rules ?? []).length > 0);
  }

  getLocationMutingStatus(): 'Muted' | 'Partially Muted' | null {
    if (this.isLocationWideMuted()) {
      return 'Muted';
    }
    if (this.isPartiallyMuted()) {
      return 'Partially Muted';
    }
    return null;
  }

  getLocationMutingStatusForLocation(location: Location): 'Muted' | 'Partially Muted' | null {
    if ((location.location_wide_muting_rules ?? []).length > 0) {
      return 'Muted';
    }
    if ((location.alerts ?? []).some(alert => (alert.muting_rules ?? []).length > 0)) {
      return 'Partially Muted';
    }
    return null;
  }

  getAlertTestKey(alert: Alert, index: number): string {
    const rawKey = alert.signature || alert._id || alert.event_id || `row-${index}`;
    return String(rawKey).replace(/[^a-zA-Z0-9_-]/g, '-');
  }

  getVisibleAlerts(): Alert[] {
    const alerts = this.selectedLocation?.alerts ?? [];
    if (this.showMutedAlerts) {
      return alerts;
    }
    return alerts.filter(alert => !this.isAlertMuted(alert));
  }

  toggleLocationWideMuteDropdown(): void {
    this.isLocationWideMuteDropdownOpen = !this.isLocationWideMuteDropdownOpen;
    if (this.isLocationWideMuteDropdownOpen && !this.customMuteAllUntil) {
      this.customMuteAllUntil = this.formatDateTimeLocal(new Date(Date.now() + 24 * 60 * 60 * 1000));
    }
    if (!this.isLocationWideMuteDropdownOpen) {
      this.isCustomMuteAllVisible = false;
    }
  }

  showCustomMuteAllDatePicker(): void {
    if (!this.customMuteAllUntil) {
      this.customMuteAllUntil = this.formatDateTimeLocal(new Date(Date.now() + 24 * 60 * 60 * 1000));
    }
    this.isCustomMuteAllVisible = true;
  }

  async onMuteAllForDuration(durationMs: number): Promise<void> {
    const muteUntil = new Date(Date.now() + durationMs);
    await this.createLocationWideMuteRule(muteUntil);
  }

  async onMuteAllUntilCustomDate(): Promise<void> {
    if (!this.customMuteAllUntil) {
      window.alert('Please select a custom date.');
      return;
    }

    const muteUntil = new Date(this.customMuteAllUntil);
    if (Number.isNaN(muteUntil.getTime())) {
      window.alert('Invalid custom date.');
      return;
    }

    await this.createLocationWideMuteRule(muteUntil);
  }

  async onUnmuteAllAlerts(): Promise<void> {
    if (!this.selectedLocation) {
      return;
    }

    const ruleIds = (this.selectedLocation.location_wide_muting_rules ?? []).map(rule => rule._id).filter(Boolean);
    if (ruleIds.length === 0) {
      return;
    }

    this.isMutatingAllAlerts = true;

    try {
      await firstValueFrom(this.publicApiService.deleteLocationMutingRules(this.selectedLocation.uuid, ruleIds));
      this.isLocationWideMuteDropdownOpen = false;
      await this.refreshSelectedLocation();
    } catch (err) {
      console.error('Error updating location-wide muting rules:', err);
      window.alert('Failed to update location-wide muting rules.');
    } finally {
      this.isMutatingAllAlerts = false;
    }
  }

  canMuteAlert(alert: Alert): boolean {
    return !!alert.signature;
  }

  isMuteDropdownOpen(alert: Alert): boolean {
    return this.openMuteDropdownAlertKey === this.getAlertKey(alert);
  }

  toggleMuteDropdown(alert: Alert): void {
    const alertKey = this.getAlertKey(alert);
    this.openMuteDropdownAlertKey = this.openMuteDropdownAlertKey === alertKey ? null : alertKey;
  }

  showCustomDatePicker(alert: Alert): void {
    const alertKey = this.getAlertKey(alert);
    if (!this.customMuteUntilByAlertKey[alertKey]) {
      this.customMuteUntilByAlertKey[alertKey] = this.formatDateTimeLocal(new Date(Date.now() + 24 * 60 * 60 * 1000));
    }
  }

  isCustomDatePickerVisible(alert: Alert): boolean {
    return !!this.customMuteUntilByAlertKey[this.getAlertKey(alert)];
  }

  async onUnmuteAlert(alert: Alert): Promise<void> {
    if (!this.selectedLocation) {
      return;
    }

    const alertKey = this.getAlertKey(alert);
    this.mutatingAlertKeys.add(alertKey);

    try {
      if (this.isAlertMuted(alert)) {
        const ruleIds = this.getRuleIdsToDelete(alert);
        if (ruleIds.length === 0) {
          return;
        }

        const hasLocationWideRules = (this.selectedLocation.location_wide_muting_rules ?? []).length > 0;
        if (hasLocationWideRules) {
          const shouldContinue = window.confirm('This alert is muted by a location-wide rule. Unmuting it here will remove the location-wide mute and affect all alerts for this location. Continue?');
          if (!shouldContinue) {
            return;
          }
        }

        await firstValueFrom(this.publicApiService.deleteLocationMutingRules(this.selectedLocation.uuid, ruleIds));
      }

      await this.refreshSelectedLocation();
    } catch (err) {
      console.error('Error updating muting rules:', err);
      window.alert('Failed to update muting rules.');
    } finally {
      this.mutatingAlertKeys.delete(alertKey);
    }
  }

  async onMuteAlertForDuration(alert: Alert, durationMs: number): Promise<void> {
    const muteUntil = new Date(Date.now() + durationMs);
    await this.createMuteRule(alert, muteUntil);
  }

  async onMuteAlertUntilCustomDate(alert: Alert): Promise<void> {
    const alertKey = this.getAlertKey(alert);
    const customValue = this.customMuteUntilByAlertKey[alertKey];

    if (!customValue) {
      window.alert('Please select a custom date.');
      return;
    }

    const muteUntil = new Date(customValue);
    if (Number.isNaN(muteUntil.getTime())) {
      window.alert('Invalid custom date.');
      return;
    }

    await this.createMuteRule(alert, muteUntil);
  }

  onAlertsSummaryIndicatorClicked(indicator: string) {
    const model = Object.assign({}, this.gridApi.getFilterModel());
    model['alerts'] = {
      filterType: 'text',
      type: indicator,
    };

    this.gridApi.setFilterModel(model);
    this.gridApi.onFilterChanged();
  }

  clearAllFilters(){
    this.gridApi.setFilterModel({});
    this.gridApi.onFilterChanged();
  }

  onModelUpdated(e: any) {
    this.loading = false;
    this.isFiltered = Object.keys(this.gridApi.getFilterModel()).length > 0;
    if (this.gridApi) {
      this.gridApi.refreshCells({ force: true });
    }
  }

  getAlertKey(alert: Alert): string {
    return alert._id || alert.event_id || alert.signature || `${alert.created_at}-${alert.message_type}`;
  }

  isLatestQcScanUnconfirmed(items: any[] | null | undefined): boolean {
    const latestItem = this.getLatestQcScanItem(items);
    return latestItem ? !this.isQcScanConfirmed(latestItem) : false;
  }

  isLatestQcScanItem(items: any[] | null | undefined, item: any): boolean {
    return this.getLatestQcScanItem(items) === item;
  }

  shouldShowQcConfirmToggle(items: any[] | null | undefined, item: any): boolean {
    return this.isLatestQcScanItem(items, item)
      && item?.qc_scan_error === true
      && item?.alerts_deleted !== true;
  }

  getQcConfirmModalMessage(): string {
    return this.selectedQcConfirmValue
      ? 'Do you want to confirm the QC scan?'
      : 'Do you want to unconfirm the QC scan?';
  }

  openConfirmScanModal(section: string, scope: string, item: any, qcScanConfirmed: boolean): void {
    this.selectedQcConfirmSection = section;
    this.selectedQcConfirmScope = scope;
    this.selectedQcConfirmItem = item;
    this.selectedQcConfirmValue = qcScanConfirmed;
    const modalElement = document.getElementById('confirmQcScanModal');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  closeConfirmScanModal(): void {
    const modalElement = document.getElementById('confirmQcScanModal');

    if (modalElement) {
      const modal = bootstrap.Modal.getOrCreateInstance(modalElement);
      modal.hide();

      modalElement.classList.remove('show');
      modalElement.setAttribute('aria-hidden', 'true');
      modalElement.removeAttribute('aria-modal');
      modalElement.style.display = 'none';

      document.body.classList.remove('modal-open');
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
      document.querySelectorAll('.modal-backdrop').forEach(backdrop => backdrop.remove());
    }

    this.selectedQcConfirmSection = '';
    this.selectedQcConfirmScope = '';
    this.selectedQcConfirmItem = null;
    this.selectedQcConfirmValue = false;
  }

  async onConfirmScanModalYes(): Promise<void> {
    if (!this.selectedLocation || !this.selectedQcConfirmScope || !this.selectedQcConfirmItem?._id) {
      this.closeConfirmScanModal();
      return;
    }

    this.isUpdatingQcScanConfirmation = true;
    try {
      await this.publicApiService.updateLocationQcScanConfirmation(
        this.selectedLocation.uuid,
        this.selectedQcConfirmScope,
        this.selectedQcConfirmItem._id,
        this.selectedQcConfirmValue,
      );
      const qcData = await this.publicApiService.getLocationQcData(this.selectedLocation.uuid);
      this.selectedLocationDetails = {
        ...this.selectedLocationDetails,
        qcData,
      };
      this.closeConfirmScanModal();
    } catch (err) {
      console.error('Error updating QC scan confirmation:', err);
      window.alert('Failed to update QC scan confirmation.');
    } finally {
      this.isUpdatingQcScanConfirmation = false;
    }
  }

  isQcScanConfirmed(item: any): boolean {
    return item?.qc_scan_confirmed === true || item?.qc_scan_confirmed === 'true' || item?.qc_scan_confirmed === 'Yes';
  }

  private getLatestQcScanItem(items: any[] | null | undefined): any | null {
    if (!items || items.length === 0) {
      return null;
    }

    return items.reduce((latest, item) => {
      const latestTime = this.getQcScanTimestamp(latest);
      const itemTime = this.getQcScanTimestamp(item);
      return itemTime > latestTime ? item : latest;
    }, items[0]);
  }

  private getQcScanTimestamp(item: any): number {
    const timestamp = Date.parse(item?.qc_scan_date ?? item?.ts_modified ?? item?.ts_created ?? '');
    return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
  }

  private getRuleIdsToDelete(alert: Alert): string[] {
    const alertRuleIds = (alert.muting_rules ?? []).map(rule => rule._id);
    const locationWideRuleIds = (this.selectedLocation?.location_wide_muting_rules ?? []).map(rule => rule._id);
    return [...new Set([...alertRuleIds, ...locationWideRuleIds].filter(Boolean))];
  }

  private async refreshSelectedLocation(): Promise<void> {
    if (!this.selectedLocation) {
      return;
    }

    const refreshedLocation = await firstValueFrom(this.publicApiService.getLocationDetails(this.selectedLocation.uuid));
    if (!refreshedLocation) {
      return;
    }

    this.selectedLocation = refreshedLocation;
  }

  private async createMuteRule(alert: Alert, muteUntil: Date): Promise<void> {
    if (!this.selectedLocation) {
      return;
    }
    if (!alert.signature) {
      window.alert('This alert cannot be muted because it has no signature.');
      return;
    }

    const alertKey = this.getAlertKey(alert);
    this.mutatingAlertKeys.add(alertKey);

    try {
      await firstValueFrom(this.publicApiService.createLocationMutingRules(this.selectedLocation.uuid, [alert.signature], new Date().toISOString(), muteUntil.toISOString()));
      delete this.customMuteUntilByAlertKey[alertKey];
      this.openMuteDropdownAlertKey = null;
      await this.refreshSelectedLocation();
    } catch (err) {
      console.error('Error updating muting rules:', err);
      window.alert('Failed to update muting rules.');
    } finally {
      this.mutatingAlertKeys.delete(alertKey);
    }
  }

  private async createLocationWideMuteRule(muteUntil: Date): Promise<void> {
    if (!this.selectedLocation) {
      return;
    }

    this.isMutatingAllAlerts = true;

    try {
      await firstValueFrom(this.publicApiService.createLocationMutingRules(this.selectedLocation.uuid, [], new Date().toISOString(), muteUntil.toISOString()));
      this.customMuteAllUntil = '';
      this.isLocationWideMuteDropdownOpen = false;
      this.isCustomMuteAllVisible = false;
      await this.refreshSelectedLocation();
    } catch (err) {
      console.error('Error updating location-wide muting rules:', err);
      window.alert('Failed to update location-wide muting rules.');
    } finally {
      this.isMutatingAllAlerts = false;
    }
  }

  private formatDateTimeLocal(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    const hours = `${date.getHours()}`.padStart(2, '0');
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }
}
